import { CalendarDate, DateValue } from '@internationalized/date';
import { SetStateAction, WritableAtom, atom } from 'jotai';
import { atomWithLocation } from 'jotai-location';
import mapValues from 'lodash/mapValues';
import { match, compile } from 'path-to-regexp';

import { PathParams } from '../routing/types';

type SetLocationAtomOptions = { replace?: boolean };

type Location = {
  pathname?: string;
  searchParams?: URLSearchParams;
  hash?: string;
};

let isInternalNavigation = false;

const subscribeToNavigation = (callback: () => void) => {
  const handler = () => {
    if (!isInternalNavigation) callback();
  };
  window.addEventListener('popstate', handler);
  window.addEventListener('pushState', handler);
  window.addEventListener('replaceState', handler);
  window.addEventListener('hashchange', handler);
  return () => {
    window.removeEventListener('popstate', handler);
    window.removeEventListener('pushState', handler);
    window.removeEventListener('replaceState', handler);
    window.removeEventListener('hashchange', handler);
  };
};

const applyLocation = (
  location: Location,
  options?: { replace?: boolean },
): void => {
  const url = new URL(window.location.href);
  if (location.pathname !== undefined) url.pathname = location.pathname;
  if (location.searchParams !== undefined)
    url.search = location.searchParams.toString();
  if (location.hash !== undefined) url.hash = location.hash;

  isInternalNavigation = true;
  try {
    if (options?.replace) {
      window.history.replaceState(window.history.state, '', url);
    } else {
      window.history.pushState(null, '', url);
    }
  } finally {
    isInternalNavigation = false;
  }
};

export const $location: WritableAtom<
  Location,
  [SetStateAction<Location>, SetLocationAtomOptions?],
  void
> = atomWithLocation({ subscribe: subscribeToNavigation, applyLocation });

/**
 * Create an atom from a query string parameter.
 *
 * @param param - The key of the query string parameter.
 * @param options - The options for the atom.
 * @returns An atom that can be used to get and set the query string parameter.
 */
const createAtomFromQueryString = <
  T = string,
  HasDefault extends boolean = false,
>(
  param: string,
  options?: {
    parse?: (value: string) => T | undefined;
    serialize?: (value: T | undefined) => string | undefined;
    defaultValue?: T;
  },
): WritableAtom<
  HasDefault extends true ? T : T | undefined,
  [T | undefined, SetLocationAtomOptions?],
  void
> => {
  const newAtom = atom(
    (get): HasDefault extends true ? T : T | undefined => {
      const location = get($location);
      const rawValue = location.searchParams?.get(param) ?? '';
      const result =
        rawValue === ''
          ? options?.defaultValue
          : options?.parse
            ? options.parse(rawValue)
            : (rawValue as T);

      return result as HasDefault extends true ? T : T | undefined;
    },
    (get, set, value: T | undefined, setOptions?: SetLocationAtomOptions) => {
      const location = get($location);

      const newSearchParams = new URLSearchParams(location.searchParams);

      if (value === undefined || value === null || value === '') {
        newSearchParams.delete(param);
      } else {
        const serializedValue = options?.serialize
          ? options.serialize(value)
          : String(value);

        if (serializedValue !== undefined) {
          newSearchParams.set(param, serializedValue);
        } else {
          newSearchParams.delete(param);
        }
      }

      set(
        $location,
        {
          pathname: location.pathname,
          searchParams: newSearchParams,
          ...(location.hash ? { hash: location.hash } : {}),
        },
        { replace: !!setOptions?.replace },
      );
    },
  );

  newAtom.debugLabel = `$${param}`;

  return newAtom;
};

/**
 * Presets for common query string parameter types.
 */
export const atomFromQueryString = {
  string: <T extends string = string>(key: string, defaultValue?: T) =>
    createAtomFromQueryString<T, T extends undefined ? false : true>(key, {
      defaultValue,
    }),

  stringSet: <T extends string = string>(key: string) =>
    createAtomFromQueryString<Set<T>, Set<T> extends undefined ? false : true>(
      key,
      {
        defaultValue: new Set<T>(),
        parse: (value) => new Set(value.split('..').map((v) => v as T)),
        serialize: (value) => {
          const arr = Array.from(value ?? []);
          return arr.length > 0 ? arr.join('..') : undefined;
        },
      },
    ),

  enum: <T extends string>(key: string, defaultValue?: T) =>
    createAtomFromQueryString<T, T extends undefined ? false : true>(key, {
      defaultValue,
    }),

  number: <TDefault extends number | undefined = undefined>(
    key: string,
    defaultValue?: TDefault,
  ) =>
    createAtomFromQueryString<
      number,
      TDefault extends undefined ? false : true
    >(key, {
      parse: (value) => {
        const parsed = Number(value);
        return isNaN(parsed) ? undefined : parsed;
      },
      serialize: (value) => String(value),
      defaultValue,
    }),

  date: <TDefault extends Date | undefined = undefined>(
    key: string,
    defaultValue?: TDefault,
  ) =>
    createAtomFromQueryString<Date, TDefault extends undefined ? false : true>(
      key,
      {
        parse: (value) => {
          const parsed = new Date(value);
          return isNaN(parsed.getTime()) ? undefined : parsed;
        },
        serialize: (value) => value?.toISOString().split('T')[0],
        defaultValue,
      },
    ),

  dateTime: <TDefault extends Date | undefined = undefined>(
    key: string,
    defaultValue?: TDefault,
  ) =>
    createAtomFromQueryString<Date, TDefault extends undefined ? false : true>(
      key,
      {
        parse: (value) => {
          const parsed = new Date(value);
          return isNaN(parsed.getTime()) ? undefined : parsed;
        },
        serialize: (value) => value?.toISOString(),
        defaultValue,
      },
    ),

  boolean: <TDefault extends boolean | undefined = undefined>(
    key: string,
    defaultValue?: TDefault,
  ) =>
    createAtomFromQueryString<
      boolean,
      TDefault extends undefined ? false : true
    >(key, {
      parse: (value) => value === 'true',
      serialize: (value) => String(value),
      defaultValue,
    }),

  custom: <T, TDefault extends T | undefined = undefined>(
    key: string,
    options: {
      parse: (value: string) => T | undefined;
      serialize: (value: T | undefined) => string | undefined;
      defaultValue?: TDefault;
    },
  ) =>
    createAtomFromQueryString<T, TDefault extends undefined ? false : true>(
      key,
      options,
    ),

  dateRange: <
    TDefault extends { start: DateValue; end: DateValue } | null = null,
  >(
    defaultValue?: TDefault,
  ) => {
    type DateRange = { start: DateValue; end: DateValue };

    const parseCalendarDateFromString = (
      dateString: string,
    ): CalendarDate | null => {
      try {
        const [year, month, day] = dateString.split('-').map(Number);
        if (!year || !month || !day) return null;
        return new CalendarDate(year, month, day);
      } catch {
        return null;
      }
    };

    const formatCalendarDateToString = (date: DateValue): string => {
      if ('year' in date && 'month' in date && 'day' in date) {
        const year = date.year.toString().padStart(4, '0');
        const month = date.month.toString().padStart(2, '0');
        const day = date.day.toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return '';
    };

    return atom(
      (get): TDefault extends null ? DateRange | null : DateRange => {
        const location = get($location);
        const fromParam = location.searchParams?.get('from') ?? '';
        const toParam = location.searchParams?.get('to') ?? '';

        if (!fromParam && !toParam) {
          return (defaultValue ?? null) as TDefault extends null
            ? DateRange | null
            : DateRange;
        }

        const start = fromParam ? parseCalendarDateFromString(fromParam) : null;
        const end = toParam ? parseCalendarDateFromString(toParam) : null;

        // DateRangePicker requires both start and end to be defined
        // If only one is provided, we'll create a range where both are the same
        if (start && !end) {
          return { start, end: start } as TDefault extends null
            ? DateRange | null
            : DateRange;
        }
        if (end && !start) {
          return { start: end, end } as TDefault extends null
            ? DateRange | null
            : DateRange;
        }
        if (start && end) {
          return { start, end } as TDefault extends null
            ? DateRange | null
            : DateRange;
        }

        return (defaultValue ?? null) as TDefault extends null
          ? DateRange | null
          : DateRange;
      },
      (
        get,
        set,
        value: DateRange | null,
        setOptions?: SetLocationAtomOptions,
      ) => {
        const location = get($location);
        const newSearchParams = new URLSearchParams(location.searchParams);

        if (!value) {
          newSearchParams.delete('from');
          newSearchParams.delete('to');
        } else {
          if (value.start) {
            newSearchParams.set(
              'from',
              formatCalendarDateToString(value.start),
            );
          } else {
            newSearchParams.delete('from');
          }

          if (value.end) {
            newSearchParams.set('to', formatCalendarDateToString(value.end));
          } else {
            newSearchParams.delete('to');
          }
        }

        set(
          $location,
          {
            pathname: location.pathname,
            searchParams: newSearchParams,
            ...(location.hash ? { hash: location.hash } : {}),
          },
          { replace: !!setOptions?.replace },
        );
      },
    );
  },
};

const $locationPathname = atom((get) => get($location).pathname);

export const atomFromRoutePath = <
  P extends string,
  O extends Record<keyof PathParams<P>, unknown> = Record<
    keyof PathParams<P>,
    string
  >,
>(
  pathPattern: P,
  parse?: (value: PathParams<P>) => O | undefined,
  serialize?: (value: Partial<O>) => PathParams<P>,
) => {
  const toPath = compile(pathPattern);
  return atom(
    (get): O & { __didMatch: boolean } => {
      const pathname = get($locationPathname);
      const matchResult = match(pathPattern, { end: false })(pathname ?? '');

      if (!matchResult)
        return { __didMatch: false } as O & { __didMatch: boolean };

      const result = parse
        ? (mapValues(matchResult.params, parse) as O)
        : (matchResult.params as O);

      return {
        __didMatch: true,
        ...result,
      };
    },
    (_, set, value: Partial<O>, setOptions?: SetLocationAtomOptions) => {
      const newPathname = toPath(
        (serialize ? serialize(value) : mapValues(value, String)) as never,
      );

      set(
        $location,
        {
          pathname: newPathname,
        },
        setOptions,
      );
    },
  );
};
